import {Alert, Button} from "reactstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import React, {useMemo} from "react";
import moment from "moment/moment";
import classNames from 'classnames';

import BadgeWithIcon from "../BadgeWithIcon";
import {BADGE_TYPES, PROPOSAL_STATUSES, PROPOSAL_VIEW} from "../../../services/appConstants";
import {SplitButton} from "primereact/splitbutton";

export const createActionFormatter = ({view, canEdit, editBuilding, approveLine, declineLine}) => {
    return (item) => useMemo(() => {
        const isApproved = item.statusId === PROPOSAL_STATUSES.SENT.value;
        const isDeclined = item.statusId === PROPOSAL_STATUSES.VIEWED.value;
        

        if (view === PROPOSAL_VIEW.EXPIRING) {
            return (<>
                <Button
                    size="sm"
                    outline={!isApproved}
                    className="fs--1 mr-2"
                    color={isApproved ? "success" : "secondary"}
                    disabled={!canEdit}
                    onClick={() => approveLine(item)}>
                    <FontAwesomeIcon icon="check" className="fs--4"/>
                </Button>
                <Button
                    size="sm"
                    outline={!isDeclined}
                    className="fs--1"
                    color={isDeclined ? "danger" : "secondary"}
                    disabled={!canEdit}
                    onClick={() => declineLine(item)}>
                    <FontAwesomeIcon icon="ban" className="fs--4"/>
                </Button>
            </>);
        }
        
        return null;
        }, [item, view, canEdit, editBuilding, approveLine, declineLine]);
}


export const createEditButtonFormatter = ({canEdit, editDevice, view}) => {
    return (item) => useMemo(() => {
        const isApproved = item.statusId === PROPOSAL_STATUSES.SENT.value;
        const isDeclined = item.statusId === PROPOSAL_STATUSES.VIEWED.value;
        
        return (
            <>
                {view === PROPOSAL_VIEW.EXPIRING && <span className={classNames(
                    "fs--3 rounded-capsule badge",
                    {
                        'badge-soft-success': isApproved,
                        'badge-soft-danger': isDeclined,
                        'badge-soft-secondary': !isApproved && !isDeclined,
                    }
                )}>{isApproved ? "Approved" : (isDeclined ? "Declined" : "Approval required")}</span>}
            </>);
    }, [item, canEdit, editDevice, view]);
};

export const createButtonHeader = ({view, canEdit, updateAll}) => {
    return () => useMemo(() => {
        if (view !== PROPOSAL_VIEW.EXPIRING) return null;

        const items = [
            {
                label: 'Approve all',
                icon: 'pi pi-check',
                command: () => updateAll(PROPOSAL_STATUSES.VIEWED.value)
            },
            {
                label: 'Decline all',
                icon: 'pi pi-times',
                command: () => updateAll(PROPOSAL_STATUSES.VIEWED.value)
            }
        ];

        return (
            <SplitButton
                label="Approve all"
                className="small-font rounded fs--1"
                size={'small'}
                onClick={() => updateAll(PROPOSAL_STATUSES.SENT.value)}
                icon={null}
                model={items}
                disabled={!canEdit}
            />
        );
    }, [view, canEdit, updateAll]);
};

const warningAlert = (item) => {
    return (<Alert color="warning" style={{maxWidth: "fit-content"}}>{item}</Alert>);
};

export const corpNameFormat = (item) => {
    if (item.corpName !== item.auditCorpName) {
        return warningAlert(item.auditCorpName);
    }

    return item.auditCorpName;
};

export const elevatorFormat = (item, isBuildingLevel) => {
    let elevatorCompanyName = item.auditElevatorCompany;
    let hasMultipleDevices = false;

    if (isBuildingLevel) {
        hasMultipleDevices = item.devices.length > 1 && !item.devices.every(d => d.elevatorCompanyId === item.elevatorCompanyId);
        elevatorCompanyName = hasMultipleDevices ? "Multiple" : item.auditElevatorCompany;
    }

    if (item.elevatorCompany !== item.auditElevatorCompany || hasMultipleDevices) {
        return warningAlert(elevatorCompanyName);
    }

    return elevatorCompanyName;
};

export const superFormat = (item) => {
    if (item.superName !== item.auditSuperName) {
        return warningAlert(item.auditSuperName);
    }

    return item.auditSuperName;
}

export const superPhoneFormat = (item) => {
    if (item.superPhone !== item.auditSuperPhone) {
        return warningAlert(item.auditSuperPhone);
    }

    return item.auditSuperPhone;
}

export const yesNoFormatter = (auditValue, originalValue, isUnderContract) => {
    let badgeType = BADGE_TYPES.SUCCESS;
    let tooltipText = null;

    if (!auditValue) {
        badgeType = BADGE_TYPES.DANGER;
    } else if (isUnderContract && originalValue) {
        badgeType = BADGE_TYPES.SECONDARY;
        tooltipText = "Already contracted – cannot remove.";
    }
    
    return <BadgeWithIcon
        type={badgeType}
        tooltip={tooltipText}
        style={{width: "40px"}}
        text={auditValue ? "Yes" : "No"}
    />;
};

export const showQceFormatter = (item) => {
    let badgeType = BADGE_TYPES.SUCCESS;
    let tooltipText = null;

    if (!item.auditQce) {
        badgeType = BADGE_TYPES.DANGER;
    } else if (item.isUnderContract && item.qce) {
        badgeType = BADGE_TYPES.SECONDARY;
        tooltipText = "Already contracted – cannot downscale.";
    }

    return <BadgeWithIcon
        type={badgeType}
        tooltip={tooltipText}
        style={{width: "40px"}}
        text={item.auditQce ? item.auditQceFrequency : "No"}
    />;
};

export const inspTypeColumn = (item) => {
    return <Alert color={item.isPeriodic ? "primary" : "info"}
                  className="text-center">{item.isPeriodic ? "Category & Periodic" : "Category"}</Alert>;
};

export const yearFormat = (item, proposalYear) => {
    const currentYear = proposalYear ? proposalYear : moment(new Date()).year();

    if (item.agreementEnd <= currentYear) {
        return (<Alert color={"danger"} style={{maxWidth: "fit-content"}}>
            Expires in {item.agreementEnd}
        </Alert>);
    }

    return (<Alert color={"success"}>
        {item.agreementStart} - {item.agreementEnd}
    </Alert>);
};